/**
 * Engine counters across the top of every Discounts tab -- phase 6b, Task 1.
 * GET /admin/discounts/stats (live counts, safe to refetch on focus).
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, Box, Card, Skeleton, Typography } from '@mui/material';
import { discountsApi, qk } from './api';
import { toApiFailure } from './errors';
import type { StatsResponse } from './types';

const TILES: Array<{ key: keyof StatsResponse; label: string }> = [
  { key: 'activeManualPromotions', label: 'Active promotions' },
  { key: 'redemptionsLast30Days', label: 'Redemptions (30d)' },
  { key: 'loyaltyCards', label: 'Loyalty cards' },
  { key: 'rewardsIssued', label: 'Rewards issued' },
  { key: 'rewardsRedeemed', label: 'Rewards redeemed' },
  { key: 'referralCodesIssued', label: 'Referral codes' },
  { key: 'referralsConverted', label: 'Referrals converted' },
];

export const StatsBar: React.FC = () => {
  const { data, isLoading, error } = useQuery({ queryKey: qk.stats, queryFn: discountsApi.stats });

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }} data-testid="discounts-stats-error">
        Could not load engine stats: {toApiFailure(error).message}
      </Alert>
    );
  }

  return (
    <Box
      data-testid="discounts-stats"
      sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 1.5, mb: 2 }}
    >
      {TILES.map(({ key, label }) => (
        <Card key={key} variant="outlined" sx={{ px: 2, py: 1.25 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3 }}>
            {label}
          </Typography>
          {isLoading || !data ? (
            <Skeleton width={48} height={32} />
          ) : (
            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.4 }}>
              {data[key]}
            </Typography>
          )}
        </Card>
      ))}
    </Box>
  );
};

export default StatsBar;
